import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Address } from "@/types/address.types";
import { addressRepository } from "@/db/repositories/address.repository";
import { CSVImportService, ImportResult } from "@/services/csv/csv-import.service";
import { logRepository } from "@/db/repositories/log.repository";
import { ADIFParserService } from "@/services/adif/adif-parser.service";
import { db } from "@/db/db";
import { formatCall } from "@/utils/string.utils";
import { ExportOptions, ImportOptions } from "@/types/export.types";
import { addressExportService } from "@/services/export/address-export.service";
import { addressImportService } from "@/services/import/address-import.service";

interface AddressState {
  addresses: Address[];
  loading: boolean;
  error: string | null;
  selectedIds: string[];
}

const initialState: AddressState = {
  addresses: [],
  loading: false,
  error: null,
  selectedIds: [],
};

export const fetchAddresses = createAsyncThunk("addresses/fetchAddresses", async () => {
  return await addressRepository.findAll();
});

export const importAddresses = createAsyncThunk(
  "addresses/importAddresses",
  async (file: File, { rejectWithValue, dispatch }) => {
    try {
      // 获取文件扩展名并转换为小写
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
      let result: ImportResult;
      
      // 根据文件扩展名选择使用的导入服务
      if (fileExtension === 'csv') {
        const service = new CSVImportService();
        result = await service.importCSV(file);
      } else if (fileExtension === 'adif' || fileExtension === 'adi') {
        // 处理ADIF文件
        const adifParser = new ADIFParserService();
        const qsoRecords = await adifParser.parseADIFFileToQSORecords(file);
        
        // 从QSO记录中提取呼号，去重
        const uniqueCalls = [...new Set(qsoRecords.map(qso => formatCall(qso.call)))];
        
        // 获取现有地址，避免重复
        const existingAddresses = await addressRepository.findAll();
        const existingCallMap = new Map(existingAddresses.map(addr => [addr.call, addr]));
        
        // 为每个呼号创建地址记录
        const now = new Date().toISOString();
        const addressesToImport: Address[] = [];
        
        for (const call of uniqueCalls) {
          if (!existingCallMap.has(call)) {
            // 新呼号，创建地址记录
            addressesToImport.push({
              id: `address_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              call,
              address: '',
              zip: '',
              status: 'valid',
              outsea: false,
              createdAt: now,
              updatedAt: now,
            });
          }
        }
        
        // 批量导入地址记录
        let successCount = 0;
        let failureCount = 0;
        
        if (addressesToImport.length > 0) {
          try {
            await db.addresses.bulkPut(addressesToImport);
            successCount = addressesToImport.length;
          } catch {
            failureCount = addressesToImport.length;
          }
        }
        
        result = {
          successCount,
          failureCount
        };
      } else {
        throw new Error(`不支持的文件类型: ${fileExtension}。仅支持 .csv、.adif 和 .adi 文件。`);
      }
      
      // 导入完成后刷新地址列表
      void dispatch(fetchAddresses());
      return result;
    } catch (error) {
      await logRepository.create({
        operation: "import",
        resource: "address",
        status: "failed",
        error: error instanceof Error ? error.message : "未知错误",
      });
      return rejectWithValue(error instanceof Error ? error.message : "未知错误");
    }
  }
);

export const deleteAddress = createAsyncThunk(
  "addresses/deleteAddress",
  async (id: string, { rejectWithValue }) => {
    try {
      await addressRepository.delete(id);
      await logRepository.create({
        operation: "delete",
        resource: "address",
        status: "success",
        details: `删除地址 ID: ${id}`,
      });
      return id;
    } catch (error) {
      await logRepository.create({
        operation: "delete",
        resource: "address",
        status: "failed",
        error: error instanceof Error ? error.message : "未知错误",
      });
      return rejectWithValue(error instanceof Error ? error.message : "未知错误");
    }
  }
);

export const createAddress = createAsyncThunk(
  "addresses/createAddress",
  async (addressData: Omit<Address, "id" | "createdAt" | "updatedAt">, { rejectWithValue, dispatch }) => {
    try {
      const newAddress = await addressRepository.create(addressData);
      await logRepository.create({
        operation: "create",
        resource: "address",
        status: "success",
        details: `创建地址: ${addressData.call}`,
      });
      void dispatch(fetchAddresses());
      return newAddress;
    } catch (error) {
      await logRepository.create({
        operation: "create",
        resource: "address",
        status: "failed",
        error: error instanceof Error ? error.message : "未知错误",
      });
      return rejectWithValue(error instanceof Error ? error.message : "未知错误");
    }
  }
);

export const updateAddress = createAsyncThunk(
  "addresses/updateAddress",
  async ({ id, updates }: { id: string; updates: Partial<Address> }, { rejectWithValue, dispatch }) => {
    try {
      await addressRepository.update(id, updates);
      await logRepository.create({
        operation: "update",
        resource: "address",
        status: "success",
        details: `更新地址 ID: ${id}`,
      });
      void dispatch(fetchAddresses());
      return { id, updates };
    } catch (error) {
      await logRepository.create({
        operation: "update",
        resource: "address",
        status: "failed",
        error: error instanceof Error ? error.message : "未知错误",
      });
      return rejectWithValue(error instanceof Error ? error.message : "未知错误");
    }
  }
);

export const exportAddresses = createAsyncThunk(
  "addresses/exportAddresses",
  async (options: ExportOptions, { rejectWithValue, dispatch }) => {
    console.log("[Redux] exportAddresses 开始，选项:", options);
    try {
      const result = await addressExportService.exportAddresses(options);
      console.log("[Redux] exportAddresses 服务调用成功，结果:", result);
      await logRepository.create({
        operation: "export",
        resource: "address",
        status: "success",
        details: `导出 ${result.count} 条地址到 ${result.filename}`,
      });
      console.log("[Redux] 开始刷新地址列表");
      void dispatch(fetchAddresses());
      console.log("[Redux] exportAddresses 完成");
      return result;
    } catch (error) {
      console.error("[Redux] exportAddresses 失败:", error);
      await logRepository.create({
        operation: "export",
        resource: "address",
        status: "failed",
        error: error instanceof Error ? error.message : "未知错误",
      });
      return rejectWithValue(error instanceof Error ? error.message : "未知错误");
    }
  }
);

export const importAddressesFile = createAsyncThunk(
  "addresses/importAddressesFile",
  async (
    { file, options, onProgress }: { file: File; options: ImportOptions; onProgress?: (progress: number, total: number) => void },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const result = await addressImportService.importFile(file, options, onProgress);
      await logRepository.create({
        operation: "import",
        resource: "address",
        status: "success",
        details: `成功导入 ${result.successCount} 条地址，失败 ${result.failureCount} 条，跳过重复 ${result.duplicateCount} 条`,
      });
      void dispatch(fetchAddresses());
      return result;
    } catch (error) {
      await logRepository.create({
        operation: "import",
        resource: "address",
        status: "failed",
        error: error instanceof Error ? error.message : "未知错误",
      });
      return rejectWithValue(error instanceof Error ? error.message : "未知错误");
    }
  }
);

const addressSlice = createSlice({
  name: "addresses",
  initialState,
  reducers: {
    setSelectedIds: (state, action: PayloadAction<string[]>) => {
      state.selectedIds = action.payload;
    },
    clearSelectedIds: (state) => {
      state.selectedIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.loading = false;
        state.addresses = action.payload;
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(importAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(importAddresses.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(importAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAddress.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAddress.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(exportAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportAddresses.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(importAddressesFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(importAddressesFile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(importAddressesFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.addresses = state.addresses.filter((addr) => addr.id !== action.payload);
      });
  },
});

export const { setSelectedIds, clearSelectedIds } = addressSlice.actions;
export default addressSlice.reducer;
